import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import CreateButton from './CreateButton';
import UnitsGrid from './UnitsGrid';
import PageNavigator from './PageNavigator';
import UnitDetails from './UnitDetails';
import AddCustomerToUnit from './AddCustomerToUnit';
import AddNewUnit from './AddNewUnit';
import AddProject from './AddProject';
import Dialog from './Dialog';
import TextInput from './TextInput';
import { fetchProjects, createProject, deleteProject, updateProject } from '../services/projectService';
import { fetchAvailableUnits, fetchUnavailableUnits, createUnit, fetchUnitDetails, updateUnit } from '../services/unitService';
import { showSuccessNotification, showErrorNotification } from '../store/slices/notificationSlice';
import styles from './ProjectsTabContent.module.css';
import UnitUpdates from './UnitUpdates';

const UNITS_PER_PAGE = 16;

const ProjectsTabContent = ({ selectedTab }) => {
  const navigate = useNavigate();
  const { projectId, unitId } = useParams();
  const dispatch = useDispatch();

  // Get project and unit permissions from cached permissions
  const {
    hasAddProject,
    hasUpdateProject,
    hasDeleteProject,
    hasShowUnit,
    hasAddUnit,
    hasDeleteUnit,
    hasUpdateUnit,
    hasAddCustomerUnit,
    hasShowUnitUpdates,
    hasAddUnitUpdates,
    hasDeleteUnitUpdates,
    hasUpdateUnitUpdates
  } = useCachedPermissions();

  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showAddUnit, setShowAddUnit] = useState(false);
  const [unitsFilter, setUnitsFilter] = useState('available'); // 'available', 'unavailable'
  const [currentPage, setCurrentPage] = useState(1);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddProject, setShowAddProject] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editProjectName, setEditProjectName] = useState('');
  const [editProjectLoading, setEditProjectLoading] = useState(false);
  const [editProjectError, setEditProjectError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 16,
    results: 0,
    last_page: 1
  });

  // New state for units data
  const [unitsData, setUnitsData] = useState({
    available: [],
    unavailable: []
  });
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsError, setUnitsError] = useState(null);
  const [unitsPagination, setUnitsPagination] = useState({
    total: 0,
    page: 1,
    limit: 16,
    results: 0,
    last_page: 1
  });

  // Add these for unit updates navigation
  const [showUnitUpdates, setShowUnitUpdates] = useState(false);
  const [updatesUnit, setUpdatesUnit] = useState(null);

  // Define all handler functions at the top to avoid hoisting issues
  const handleAddProject = () => {
    setShowAddProject(true);
  };

  const handleSaveProject = async (projectData) => {
    try {
      await createProject(projectData);
      setShowAddProject(false);
      dispatch(showSuccessNotification('Project created successfully!'));
      // Refresh the projects list
      loadProjects(1);
    } catch (error) {
      dispatch(showErrorNotification(`Failed to create project: ${error.message}`));
      throw error; // Re-throw to let the form handle the error
    }
  };

  const handleCancelAddProject = () => {
    setShowAddProject(false);
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteProject(projectId);
      console.log('Project deleted successfully');
      dispatch(showSuccessNotification('Project deleted successfully!'));
      // Refresh the projects list
      loadProjects(1);
    } catch (error) {
      console.error('Failed to delete project:', error);
      dispatch(showErrorNotification(`Failed to delete project: ${error.message}`));
    }
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setEditProjectName(project.name);
    setEditProjectError('');
    setShowEditProject(true);
  };

  const handleSaveEditProject = async () => {
    if (!editProjectName.trim() || editProjectName.trim().length < 2) {
      setEditProjectError('Project name must be at least 2 characters long');
      return;
    }

    setEditProjectLoading(true);
    setEditProjectError('');

    try {
      await updateProject(editingProject.id, { name: editProjectName.trim() });
      console.log('Project updated successfully');

      // Update the local projects state instead of refetching
      setProjects(prevProjects =>
        prevProjects.map(project =>
          project.id === editingProject.id
            ? { ...project, name: editProjectName.trim() }
            : project
        )
      );

      // Show success notification
      dispatch(showSuccessNotification('Project updated successfully!'));

      setShowEditProject(false);
      setEditingProject(null);
      setEditProjectName('');
    } catch (error) {
      console.error('Failed to update project:', error);
      setEditProjectError(error.message);
      dispatch(showErrorNotification(`Failed to update project: ${error.message}`));
    } finally {
      setEditProjectLoading(false);
    }
  };

  const handleCancelEditProject = () => {
    setShowEditProject(false);
    setEditingProject(null);
    setEditProjectName('');
    setEditProjectError('');
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      setCurrentPage(page);
      loadProjects(page);
    }
  };

  const handleUnitClick = async (unit) => {
    // Check if user has permission to view unit details
    if (!hasShowUnit) {
      console.log('🚫 Access denied - User does not have show_unit permission');
      dispatch(showErrorNotification('You do not have permission to view unit details'));
      return;
    }

    try {
      console.log('Fetching unit details for:', unit.id);
      const response = await fetchUnitDetails(unit.id);
      console.log('Unit details fetched:', response);

      // Set the detailed unit data
      setSelectedUnit(response.data || unit);

      // Navigate to unit details URL
      navigate(`/projects/unit/${unit.id}`);
    } catch (error) {
      console.error('Failed to fetch unit details:', error);
      dispatch(showErrorNotification(`Failed to fetch unit details: ${error.message}`));
    }
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
    setSelectedUnit(null);
    setShowAddCustomer(false);
    setShowAddUnit(false);
    setUnitsFilter('available');
    setCurrentPage(1);
    // Reset units data
    setUnitsData({ available: [], unavailable: [] });
    setUnitsError(null);
    // Navigate back to projects list
    navigate('/projects');
  };

  const handleBackToUnits = () => {
    // Clear the selected unit first
    setSelectedUnit(null);
    setShowAddCustomer(false);
    setShowAddUnit(false);

    // Navigate back to units list for the current project
    if (selectedProject) {
      navigate(`/projects/project/${selectedProject.id}/units`, { replace: true });
    } else if (selectedUnit && selectedUnit.project_id) {
      // If no selectedProject but unit has project_id, navigate to that project's units
      navigate(`/projects/project/${selectedUnit.project_id}/units`, { replace: true });
    } else {
      // Fallback to projects list
      navigate('/projects', { replace: true });
    }
  };

  const handleRefreshUnit = async () => {
    if (!selectedUnit) return;

    try {
      console.log('Refreshing unit details for:', selectedUnit.id);
      const response = await fetchUnitDetails(selectedUnit.id);
      console.log('Unit details refreshed:', response);

      // Update the selected unit with fresh data
      setSelectedUnit(response.data || response);

      // Also refresh the units grid data to reflect availability changes
      if (selectedProject) {
        if (unitsFilter === 'available') {
          loadAvailableUnits(selectedProject.id, unitsPagination.page);
        } else {
          loadUnavailableUnits(selectedProject.id, unitsPagination.page);
        }
      }
    } catch (error) {
      console.error('Failed to refresh unit details:', error);
      dispatch(showErrorNotification(`Failed to refresh unit details: ${error.message}`));
    }
  };

  const handleAddCustomer = (unit) => {
    // Check if user has permission to add customer to unit
    if (!hasAddCustomerUnit) {
      console.log('🚫 Access denied - User does not have add_customer_unit permission');
      dispatch(showErrorNotification('You do not have permission to assign customers to units'));
      return;
    }
    setShowAddCustomer(true);
  };

  const handleSaveCustomer = async (data) => {
    console.log('Saving customer assignment:', data);
    setShowAddCustomer(false);

    // If save was successful, refresh unit details and navigate back to it
    if (data.success && data.unitId) {
      try {
        // Fetch the updated unit details
        const response = await fetchUnitDetails(data.unitId);
        setSelectedUnit(response.data || response);
        
        // Navigate to the unit details page
        navigate(`/projects/unit/${data.unitId}`);
        
        // Optionally refresh the units list in the background
        if (selectedProject) {
          if (unitsFilter === 'available') {
            loadAvailableUnits(selectedProject.id, unitsPagination.page);
          } else {
            loadUnavailableUnits(selectedProject.id, unitsPagination.page);
          }
        }
      } catch (error) {
        console.error('Failed to load unit details:', error);
        dispatch(showErrorNotification(`Failed to load unit details: ${error.message}`));
        // Fallback to units list if unit details can't be loaded
        setSelectedUnit(null);
        if (selectedProject) {
          navigate(`/projects/project/${selectedProject.id}/units`);
        }
      }
    } else {
      // If no success or unitId, go back to project units
      setSelectedUnit(null);
      if (selectedProject) {
        navigate(`/projects/project/${selectedProject.id}/units`);
      }
    }
  };

  const handleCancelAddCustomer = () => {
    setShowAddCustomer(false);
    // Navigate back to units list for the current project
    if (selectedProject) {
      navigate(`/projects/project/${selectedProject.id}/units`);
    }
  };

  const handleEditUnit = async (updatedUnit) => {
    try {
      console.log('Updating unit:', updatedUnit);

      // Update the selected unit with the new data immediately
      setSelectedUnit(updatedUnit);

      // Refresh the units list to reflect the changes
      if (unitsFilter === 'available') {
        loadAvailableUnits(selectedProject.id, unitsPagination.page);
      } else {
        loadUnavailableUnits(selectedProject.id, unitsPagination.page);
      }
    } catch (error) {
      console.error('Failed to update unit:', error);
      dispatch(showErrorNotification(`Failed to update unit: ${error.message}`));
    }
  };

  const handleDeleteUnit = async (unitId) => {
    try {
      console.log('Deleting unit:', unitId);
      // Refresh the units list to reflect the deletion
      if (unitsFilter === 'available') {
        loadAvailableUnits(selectedProject.id, unitsPagination.page);
      } else {
        loadUnavailableUnits(selectedProject.id, unitsPagination.page);
      }
    } catch (error) {
      console.error('Failed to handle unit deletion:', error);
    }
  };

  const handleAddUnit = () => {
    // Check if user has permission to add units
    if (!hasAddUnit) {
      console.log('🚫 Access denied - User does not have add_unit permission');
      dispatch(showErrorNotification('You do not have permission to add units'));
      return;
    }
    setShowAddUnit(true);
  };

  const handleSaveUnit = async (formData) => {
    try {
      console.log('Saving unit with FormData');

      await createUnit(formData);
      setShowAddUnit(false);
      dispatch(showSuccessNotification('Unit created successfully!'));

      // Refresh the units data
      if (unitsFilter === 'available') {
        loadAvailableUnits(selectedProject.id, 1);
      } else {
        loadUnavailableUnits(selectedProject.id, 1);
      }
    } catch (error) {
      console.error('Failed to create unit:', error);
      dispatch(showErrorNotification(`Failed to create unit: ${error.message}`));
    }
  };

  const handleCancelAddUnit = () => {
    setShowAddUnit(false);
  };

  const handleShowUnitUpdates = (unit) => {
    // Check if user has permission to view unit updates
    if (!hasShowUnitUpdates) {
      console.log('🚫 Access denied - User does not have show_unit_updates permission');
      dispatch(showErrorNotification('You do not have permission to view unit updates'));
      return;
    }
    setUpdatesUnit(unit);
    setShowUnitUpdates(true);
  };
  const handleBackFromUnitUpdates = () => {
    setShowUnitUpdates(false);
    setUpdatesUnit(null);
  };

  // Load available units for a project
  const loadAvailableUnits = async (projectId, page = 1) => {
    try {
      setUnitsLoading(true);
      setUnitsError(null);
      const response = await fetchAvailableUnits(projectId, page, UNITS_PER_PAGE);
      if (response.data && response.data.data) {
        setUnitsData(prev => ({
          ...prev,
          available: response.data.data
        }));
        setUnitsPagination({
          total: response.data.total,
          page: response.data.page,
          limit: response.data.limit,
          results: response.data.results,
          last_page: response.data.last_page
        });
      } else {
        setUnitsData(prev => ({
          ...prev,
          available: []
        }));
      }
    } catch (err) {
      setUnitsError(err);
      setUnitsData(prev => ({
        ...prev,
        available: []
      }));

      // Handle authentication errors
      if (err.message.includes('Authentication failed')) {
        navigate('/login', { replace: true });
      }
    } finally {
      setUnitsLoading(false);
    }
  };

  // Load unavailable units for a project
  const loadUnavailableUnits = async (projectId, page = 1) => {
    try {
      setUnitsLoading(true);
      setUnitsError(null);
      const response = await fetchUnavailableUnits(projectId, page, UNITS_PER_PAGE);
      if (response.data && response.data.data) {
        setUnitsData(prev => ({
          ...prev,
          unavailable: response.data.data
        }));
        setUnitsPagination({
          total: response.data.total,
          page: response.data.page,
          limit: response.data.limit,
          results: response.data.results,
          last_page: response.data.last_page
        });
      } else {
        setUnitsData(prev => ({
          ...prev,
          unavailable: []
        }));
      }
    } catch (err) {
      setUnitsError(err);
      setUnitsData(prev => ({
        ...prev,
        unavailable: []
      }));

      // Handle authentication errors
      if (err.message.includes('Authentication failed')) {
        navigate('/login', { replace: true });
      }
    } finally {
      setUnitsLoading(false);
    }
  };

  const loadProjects = async (page = 1) => {
    try {
      setLoading(true);
      const response = await fetchProjects(page);
      if (response.data && response.data.data) {
        setProjects(response.data.data);
        setPagination({
          total: response.data.total,
          page: response.data.page,
          limit: response.data.limit,
          results: response.data.results,
          last_page: response.data.last_page
        });
      } else {
        setProjects([]);
      }
    } catch (err) {
      setError(err);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only load projects initially if we don't have URL parameters that will trigger their own loading
    if (!projectId && !unitId) {
      loadProjects(1);
    }
  }, []);

  // Handle URL parameters to set initial state
  useEffect(() => {
    const handleUrlState = async () => {
      if (projectId && !selectedProject) {
        try {
          // Load project details and set as selected
          const projectsResponse = await fetchProjects(1, 100); // Load all projects to find the specific one
          const project = projectsResponse.data.data.find(p => p.id.toString() === projectId);

          if (project) {
            setSelectedProject(project);

            if (unitId && !selectedUnit) {
              // Load unit details only if not already selected
              const unitResponse = await fetchUnitDetails(unitId);
              setSelectedUnit(unitResponse.data);
            } else if (!unitId) {
              // Load units for the project
              loadAvailableUnits(project.id, 1);
            }
          }
        } catch (error) {
          console.error('Failed to load project/unit from URL:', error);
        }
      } else if (unitId && !selectedUnit && !projectId) {
        try {
          // Load unit details directly and find its project
          const unitResponse = await fetchUnitDetails(unitId);
          setSelectedUnit(unitResponse.data);

          // If unit has project info, also load the project
          if (unitResponse.data && unitResponse.data.project_id) {
            const projectsResponse = await fetchProjects(1, 100);
            const project = projectsResponse.data.data.find(p => p.id.toString() === unitResponse.data.project_id.toString());
            if (project) {
              setSelectedProject(project);
            }
          }
        } catch (error) {
          console.error('Failed to load unit from URL:', error);
        }
      }
    };

    handleUrlState();
  }, [projectId, unitId, selectedProject]);

  // If no project is selected and no unit is selected from URL, show projects grid
  if (!selectedProject && !selectedUnit) {
    return (
      <div className={styles.projectsContainer}>
        <div className={styles.headerContainer}>
          <h2 className={styles.headerTitle}>Projects</h2>
          {hasAddProject && (
            <CreateButton label="+ Add Project" onClick={handleAddProject} />
          )}
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className={styles.successMessage}>
            {successMessage}
          </div>
        )}

        {loading ? (
          <div className={styles.loadingMessage}>
            <h3>Loading...</h3>
            <p>Fetching projects...</p>
          </div>
        ) : error ? (
          <div className={styles.errorMessage}>
            <h3>Error</h3>
            <p>{error.message}</p>
            <button onClick={() => loadProjects(1)} className={styles.retryButton}>
              Retry
            </button>
          </div>
        ) : projects.length === 0 ? (
          <div className={styles.noProjectsMessage}>
            <h3>No Projects</h3>
            <p>There are currently no projects to display.</p>
          </div>
        ) : (
          <>
            <div className={styles.projectsGrid}>
              {projects.map((project) => (
                <div
                  key={project.id}
                  className={styles.projectCard}
                  onClick={() => {
                    // Navigate to project units URL - let useEffect handle the loading
                    navigate(`/projects/project/${project.id}/units`);
                  }}
                >
                  <h3 className={styles.projectName}>{project.name}</h3>
                  {(hasUpdateProject || hasDeleteProject) && (
                    <div className={styles.projectActions}>
                      {hasUpdateProject && (
                        <button
                          className={styles.editProjectButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditProject(project);
                          }}
                          title="Edit Project"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m18 2 4 4L7 21l-4 1 1-4L18 2z" />
                            <path d="M13.5 7.5L16.5 10.5" />
                          </svg>
                        </button>
                      )}
                      {hasDeleteProject && (
                        <button
                          className={styles.deleteProjectButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteProject(project.id);
                          }}
                          title="Delete Project"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3,6 5,6 21,6" />
                            <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {pagination.last_page > 1 && (
              <PageNavigator
                currentPage={pagination.page}
                totalPages={pagination.last_page}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}

        {/* Add Project Dialog */}
        <Dialog
          open={showAddProject}
          onClose={handleCancelAddProject}
          title="Add New Project"
        >
          <AddProject
            onSave={handleSaveProject}
            onCancel={handleCancelAddProject}
          />
        </Dialog>

        {/* Edit Project Dialog */}
        <Dialog
          open={showEditProject}
          onClose={handleCancelEditProject}
          title="Edit Project"
        >
          <div style={{ padding: '20px', background: '#1a1a1a' }}>
            {editProjectError && (
              <div style={{
                color: '#ff6b6b',
                marginBottom: '16px',
                padding: '8px',
                background: '#2a1f1f',
                borderRadius: '4px',
                fontSize: '14px'
              }}>
                {editProjectError}
              </div>
            )}
            <TextInput
              label="Project Name"
              type="text"
              value={editProjectName}
              onChange={(e) => setEditProjectName(e.target.value)}
              placeholder="Enter project name"
            />
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleCancelEditProject}
                style={{
                  padding: '8px 16px',
                  background: 'transparent',
                  border: '1px solid #444',
                  borderRadius: '6px',
                  color: '#ccc',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEditProject}
                disabled={editProjectLoading}
                style={{
                  padding: '8px 16px',
                  background: '#FABD08',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#1a1a1a',
                  cursor: editProjectLoading ? 'not-allowed' : 'pointer',
                  opacity: editProjectLoading ? 0.6 : 1
                }}
              >
                {editProjectLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </Dialog>
      </div>
    );
  }

  // If a project is selected, show units with filter tabs
  const getCurrentUnits = () => {
    switch (unitsFilter) {
      case 'available':
        return unitsData.available;
      case 'unavailable':
        return unitsData.unavailable;
      default:
        return unitsData.available;
    }
  };

  const currentUnits = getCurrentUnits();



  // If showing add unit form, display it
  if (showAddUnit) {
    return (
      <AddNewUnit
        selectedCategory="unit"
        projectId={selectedProject.id}
        onSave={handleSaveUnit}
        onCancel={handleCancelAddUnit}
      />
    );
  }

  // If showing add customer form, display it
  if (showAddCustomer && selectedUnit) {
    return (
      <AddCustomerToUnit
        unit={selectedUnit}
        onSave={handleSaveCustomer}
        onCancel={handleCancelAddCustomer}
      />
    );
  }

  // If showing unit updates, display it
  if (showUnitUpdates && updatesUnit) {
    return (
      <UnitUpdates unit={updatesUnit} onBack={handleBackFromUnitUpdates} />
    );
  }

  // If a unit is selected, show the details page
  if (selectedUnit) {
    return (
      <UnitDetails
        unit={selectedUnit}
        onBack={handleBackToUnits}
        onEdit={handleEditUnit}
        onDelete={handleDeleteUnit}
        onAddCustomer={handleAddCustomer}
        onShowUpdates={handleShowUnitUpdates}
        onRefresh={handleRefreshUnit}
      />
    );
  }

  // Show units for selected project
  return (
    <div className={styles.projectsContainer}>
      <div className={styles.headerContainer}>
        <div className={styles.headerLeft}>
          <button className={styles.backButton} onClick={handleBackToProjects}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Projects
          </button>
          <h2 className={styles.headerTitle}>{selectedProject.name}</h2>
        </div>
        {hasAddUnit && (
          <CreateButton label="+ Add Unit" onClick={handleAddUnit} />
        )}
      </div>

      {/* Units Filter Tabs */}
      <div className={styles.unitsFilterTabs}>
        <button
          className={`${styles.filterTab} ${unitsFilter === 'available' ? styles.active : ''}`}
          onClick={() => {
            setUnitsFilter('available');
            setCurrentPage(1);
            // Load available units
            loadAvailableUnits(selectedProject.id, 1);
          }}
        >
          Available
        </button>
        <button
          className={`${styles.filterTab} ${unitsFilter === 'unavailable' ? styles.active : ''}`}
          onClick={() => {
            setUnitsFilter('unavailable');
            setCurrentPage(1);
            // Load unavailable units
            loadUnavailableUnits(selectedProject.id, 1);
          }}
        >
          Unavailable
        </button>
      </div>

      {unitsLoading ? (
        <div className={styles.loadingMessage}>
          <h3>Loading...</h3>
          <p>Fetching {unitsFilter} units...</p>
        </div>
      ) : unitsError ? (
        <div className={styles.errorMessage}>
          <h3>Error</h3>
          <p>{unitsError.message}</p>
          <button
            onClick={() => {
              if (unitsFilter === 'available') {
                loadAvailableUnits(selectedProject.id, 1);
              } else {
                loadUnavailableUnits(selectedProject.id, 1);
              }
            }}
            className={styles.retryButton}
          >
            Retry
          </button>
        </div>
      ) : currentUnits.length > 0 ? (
        <>
          <UnitsGrid units={currentUnits} onUnitClick={handleUnitClick} />
          {unitsPagination.last_page > 1 && (
            <PageNavigator
              currentPage={unitsPagination.page}
              totalPages={unitsPagination.last_page}
              onPageChange={(page) => {
                if (unitsFilter === 'available') {
                  loadAvailableUnits(selectedProject.id, page);
                } else {
                  loadUnavailableUnits(selectedProject.id, page);
                }
              }}
            />
          )}
        </>
      ) : (
        <div className={styles.noUnitsMessage}>
          <h3>No {unitsFilter === 'available' ? 'Available' : 'Unavailable'} Units</h3>
          <p>There are currently no {unitsFilter === 'available' ? 'available' : 'unavailable'} units in this project.</p>
        </div>
      )}
    </div>
  );
};

export default ProjectsTabContent; 